/* eslint-disable @typescript-eslint/no-explicit-any */
import { useRef, useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { Offcanvas } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { useTranslation } from 'react-i18next';

import {
  CashStarsIcon,
  TrashIcon,
  MoreIcon,
  ChevronRightICon,
  CategoryPlusIcon
} from 'assets/img/icons/icons';
import './dropDownMore.css';
import favoriteService from 'service/favorite';
import AddCatalog from './AddCatalog';
import { showAlert } from 'store/alert/alert.thunk';
import { cashBackActions } from 'store/cash/cash.slice';

interface DropdownOption {
  id: string;
  label: string;
  icon: any;
  hasArrow?: boolean;
}

interface DropDownMoreItemsProps {
  cashListData: any;
  setCashBackListData?: any;
}
const DropDownMoreSingleItems = ({
  cashListData,
  setCashBackListData
}: DropDownMoreItemsProps) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dispatch: Dispatch<any> = useDispatch();
  const { t } = useTranslation();

  const [isFavorite, setIsFavorite] = useState(
    cashListData?.is_favorite || false
  );
  const [isOpenMore, setIsOpenMore] = useState(false);
  const [isFavoritesModalOpen, setIsFavoritesModalOpen] = useState(false);
  const [isOpenAddToCategory, setIsOpenAddToCategory] = useState(false);

  const { data: favoriteCatalogsData, refetch } = useQuery(
    ['GET_FAVORITE_CATALOG_LIST'],
    () => {
      const res = favoriteService
        .getList({
          offset: 0,
          limit: 100
        })
        .then((res: any) => res?.catalogs);
      return res;
    }
  );

  const options: DropdownOption[] = [
    {
      id: 'add-to-favorites',
      label: 'Доб. в списку избранных',
      icon: <CashStarsIcon />
    },
    {
      id: 'delete-item',
      label: 'Удалить товар',
      icon: <TrashIcon />
    }
  ].filter(option => option.id !== 'add-to-favorites' || !isFavorite);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpenAddToCategory) return;

      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpenMore(false);
        setIsFavoritesModalOpen(false);
      }
    };

    if (isOpenMore || isFavoritesModalOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpenMore, isFavoritesModalOpen, isOpenAddToCategory]);

  const handleOptionClick = (optionId: string) => {
    if (optionId === 'delete-item' && setCashBackListData) {
      setCashBackListData((prevData: any[]) =>
        prevData.filter(item => item.id !== cashListData?.id)
      );
      dispatch(cashBackActions.removeCashBackItem(cashListData?.id));
    } else if (optionId === 'add-to-favorites') {
      setIsOpenMore(false);
      setIsFavoritesModalOpen(true);
    }
  };

  const handleOptionClickCatalog = (optionPid: number) => {
    if (optionPid) {
      favoriteService
        .createFavoriteCatalogItems({
          items: [
            {
              favorite_catalog_pid: optionPid,
              nomenclature_pid: cashListData?.nomenclature_pid
            }
          ]
        })
        .then((res: any) => {
          if (res) {
            setIsFavorite(true);
            setIsOpenMore(false);
            setIsFavoritesModalOpen(false);
            dispatch(
              showAlert({
                title: `${t('successfully_added')}`,
                type: 'success'
              })
            );
          }
        });
    }
  };

  const handleToggleClick = () => {
    setIsOpenMore(!isOpenMore);
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>
  ): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggleClick();
    } else if (event.key === 'Escape') {
      setIsOpenMore(false);
      setIsFavoritesModalOpen(false);
    }
  };

  const handleMenuKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    optionId: string
  ): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleOptionClick(optionId);
    }
  };

  const handleMenuKeyDownCatalog = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    optionId: number
  ): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleOptionClickCatalog(optionId);
    }
  };

  return (
    <div className={`main-more-dropdown more-dropdown`} ref={dropdownRef}>
      <button
        className={`more-btn ${isOpenMore ? 'active' : ''}`}
        type="button"
        onClick={handleToggleClick}
        onKeyDown={handleKeyDown}
        aria-expanded={isOpenMore}
        aria-haspopup="true"
        aria-label="More options"
      >
        <div className="more-display">
          <MoreIcon />
        </div>
      </button>
      <ul
        className={`more-dropdown-menu more-menu ${isOpenMore ? 'show' : ''}`}
        role="menu"
      >
        {options.map((option, index) => {
          return (
            <li key={option.id} role="none">
              <button
                onClick={() => handleOptionClick(option.id)}
                onKeyDown={e => handleMenuKeyDown(e, option.id)}
                className={`menu-item ${index === 0 ? 'first' : ''} ${
                  index === options.length - 1 ? 'last' : ''
                }`}
                role="menuitem"
                type="button"
              >
                <div className="menu-item-content">
                  {option.icon}
                  <div className="menu-label-container">
                    <span className="menu-label">{option.label}</span>
                  </div>
                </div>

                {option.hasArrow && <ChevronRightICon />}
              </button>
            </li>
          );
        })}
      </ul>
      {isFavoritesModalOpen && (
        <ul
          /* prettier-ignore */
          className={`more-dropdown-menu more-menu ${
            isFavoritesModalOpen ? 'show' : ''
          }`}
          role="menu"
        >
          {favoriteCatalogsData?.map((option: any, index: any) => {
            return (
              <li key={option?.pid} role="none">
                <button
                  onClick={() => handleOptionClickCatalog(option?.pid)}
                  onKeyDown={e => handleMenuKeyDownCatalog(e, option?.pid)}
                  className={`menu-item ${index === 0 ? 'first' : ''} ${
                    index === favoriteCatalogsData?.length - 1 ? 'last' : ''
                  }`}
                  role="menuitem"
                  type="button"
                >
                  <div className="menu-item-content">
                    <div className="menu-label-container">
                      <span className="menu-label">{option?.name}</span>
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
          <li role="none">
            <button
              /* prettier-ignore */
              className={`menu-item ${
                favoriteCatalogsData?.length === 0 ? 'first' : ''
              } last
              `}
              onClick={() => setIsOpenAddToCategory(true)}
              role="menuitem"
              type="button"
            >
              <div className="menu-item-content">
                <CategoryPlusIcon />
                <div className="menu-label-container">
                  <span
                    /* prettier-ignore */
                    className={`menu-label ${
                      isFavoritesModalOpen ? 'add-category' : ''
                    }`}
                  >
                    Добавить категорию
                  </span>
                </div>
              </div>
            </button>
          </li>
        </ul>
      )}

      <Offcanvas
        show={isOpenAddToCategory}
        onHide={() => {
          setIsOpenAddToCategory(false);
        }}
        placement="end"
        backdrop="static"
        keyboard={false}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title style={{ fontSize: '24px', fontWeight: '600' }}>
            Добавить категорию
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <AddCatalog
            refetch={refetch}
            setIsOpenAddToCategory={setIsOpenAddToCategory}
          />
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
};

export default DropDownMoreSingleItems;
